/**
 * Analytics consent state — the gate in front of Firebase Analytics.
 *
 * Google Consent Mode v2. Two things follow from that and are easy to get
 * wrong:
 *
 *   1. **Defaults must be set before the tag loads.** `applyConsentDefaults()`
 *      runs before `getAnalytics()` in AnalyticsService, so the SDK never
 *      writes a cookie for a visitor who hasn't agreed to one.
 *   2. **Denied is not silence.** Under Consent Mode a denied visitor still
 *      sends cookieless pings, so GA4 can model the traffic. Rejecting costs
 *      us attribution, not the whole measurement — which is why gating this
 *      properly is affordable.
 *
 * We ask only about analytics. e404 runs no advertising, so the four ad
 * signals stay denied permanently rather than being offered as a choice
 * nobody benefits from.
 */

export type ConsentChoice = 'granted' | 'denied'

/** Versioned so a future change to what we ask can re-prompt deliberately. */
export const CONSENT_KEY = 'e404.consent.v1'

/**
 * The visitor's stored decision, or `null` if they haven't made one.
 * `null` is what shows the banner.
 */
export function readConsent(): ConsentChoice | null {
  try {
    const v = window.localStorage.getItem(CONSENT_KEY)
    return v === 'granted' || v === 'denied' ? v : null
  } catch {
    // Safari private mode throws on localStorage. Treat as undecided: the
    // banner reappears, and consent stays denied. Annoying, never unlawful.
    return null
  }
}

export function writeConsent(choice: ConsentChoice): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, choice)
  } catch {
    // Non-fatal — the in-memory `setConsent` call below still applies for
    // this page view; the visitor is simply asked again next time.
  }
}

/**
 * Map our single yes/no onto the Consent Mode v2 signals.
 *
 * The four ad signals are hard-denied regardless of the answer. Granting
 * them would be claiming a permission we have no use for.
 */
function signalsFor(choice: ConsentChoice) {
  return {
    analytics_storage: choice,
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  } as const
}

/**
 * Deny everything up front. MUST be called before `getAnalytics()` — that is
 * the call that loads gtag, and Consent Mode only honours defaults that were
 * already in the dataLayer when it did.
 */
export async function applyConsentDefaults(): Promise<void> {
  try {
    // Imported on demand: a static import here put the whole Firebase chunk
    // in the entry graph, so every visitor to a marketing page downloaded it
    // at high priority whether or not analytics ever initialised.
    const { setConsent } = await import('firebase/analytics')
    setConsent(signalsFor(readConsent() ?? 'denied'))
  } catch {
    // Instrumentation must never break the app.
  }
}

/** Record and apply a decision the visitor just made. */
export function grantConsent(choice: ConsentChoice): void {
  writeConsent(choice)
  void import('firebase/analytics')
    .then((m) => m.setConsent(signalsFor(choice)))
    .catch(() => null)
}

// ── Reopening the prompt ──────────────────────────────────────────────────
//
// Consent must be as easy to withdraw as it was to give — that is a legal
// requirement, not a nicety, and without it a visitor who dismissed the
// banner has no route back. A tiny listener registry lets the footer's
// "Cookie settings" control reopen the banner from anywhere in the tree
// without threading state through providers that do not otherwise care.

type Listener = () => void
const listeners = new Set<Listener>()

/** Subscribe to reopen requests. Returns an unsubscribe function. */
export function onConsentPromptRequested(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/**
 * Ask the banner to show again, and drop the stored decision so the visitor
 * is genuinely choosing afresh rather than confirming what we already had.
 * Consent reverts to denied until they answer.
 */
export function requestConsentPrompt(): void {
  try {
    window.localStorage.removeItem(CONSENT_KEY)
  } catch {
    /* no-op — the banner still reopens for this page view */
  }
  void import('firebase/analytics')
    .then((m) => m.setConsent(signalsFor('denied')))
    .catch(() => null)
  listeners.forEach((fn) => fn())
}
